/**
 * Intent tokens — the five working colors of the system.
 *
 * Every status, every state pill, every semantic accent across every product
 * resolves through this map. Raw hex codes, raw Tailwind palette names
 * (`bg-emerald-100`, etc.), and per-product `STATUS_TONE` constants are all
 * forbidden outside `packages/ui/src/tokens/` and `packages/ui/src/blocks/`.
 *
 * To re-tune a working color globally: edit one row here. Every consumer
 * (Badge intent variant, StatusBadge, StatusDot, alerts, toasts) repaints.
 *
 * Layer mapping:
 *   info    → --primary (cobalt #318AEA)  — interactive identity
 *   success → --success (teal  #18a9b6)   — positive terminal state
 *   warning → --warning (coral #ff7b60)   — attention / in-flight
 *   danger  → --destructive (red)         — permanent negative / destructive
 *   neutral → muted-foreground            — no semantic signal
 *
 * All raw values live in `excellent/app/globals.css` (`:root` + `.dark`).
 * This module only references CSS variables — it never names a hex code.
 */

export type Intent = "info" | "success" | "warning" | "danger" | "neutral";

export type IntentSurface = "tint" | "solid" | "dot" | "border";

/**
 * Class strings for each (intent × surface) combination. Read-only —
 * consumers compose these, they do not mutate them.
 *
 * Tints reference mode-aware tokens (`--<intent>-{bg,fg,border}`) declared
 * in globals.css — light mode uses pale solid backgrounds with dark text,
 * dark mode uses translucent saturated backgrounds with luminous text.
 * Tailwind never computes opacity at runtime, so tints stay crisp in both
 * modes (the previous `bg-primary/10` pattern read muddy in dark mode).
 *
 * Solids only for filled CTAs (`info.solid` = brand Button), destructive
 * confirms (`danger.solid` = red Button), and contexts where the color
 * IS the point.
 */
export const INTENT_CLASSES: Record<Intent, Record<IntentSurface, string>> = {
  info: {
    tint: "bg-info-bg text-info-fg",
    solid: "bg-info text-primary-foreground",
    dot: "bg-info",
    border: "border-info-border",
  },
  success: {
    tint: "bg-success-bg text-success-fg",
    solid: "bg-success text-background",
    dot: "bg-success",
    border: "border-success-border",
  },
  warning: {
    tint: "bg-warning-bg text-warning-fg",
    solid: "bg-warning text-background",
    dot: "bg-warning",
    border: "border-warning-border",
  },
  danger: {
    tint: "bg-danger-bg text-danger-fg",
    solid: "bg-danger text-primary-foreground",
    dot: "bg-danger",
    border: "border-danger-border",
  },
  neutral: {
    tint: "bg-muted text-muted-foreground",
    solid: "bg-muted text-foreground",
    dot: "bg-muted-foreground/40",
    border: "border-border",
  },
};

export const INTENTS: readonly Intent[] = [
  "info",
  "success",
  "warning",
  "danger",
  "neutral",
] as const;
